/**
 * This file includes code that is a derivative work of the following open source  project:
 *
 * Name: '@okta/auth-js'
 * URL: https://github.com/okta/okta-auth-js
 * License: Apache 2.0 (https://www.apache.org/licenses/LICENSE-2.0)
 *
 * The code as been modified to meet the requirements of this project.
 */
import { Service, ServiceOptions } from './service';
import { CHANNEL_NAMES, LoggerNames } from '../lib/constants';
import { LeaderElectionService } from './leader-election.service';
import { AutoRenewalService } from './auto-renewal.service';
import { exhaustiveMatch } from '../lib/assert';
import { AuthSdkError } from '../lib/auth-sdk-error';
import { Logger } from '../lib/logger';
import { ITokenManager } from '../token-manager/token-manager.types';
import { SyncStorageService } from './sync-storage.service';
import { SingleSignoutService } from './single-signout.service';
import { V2Router } from '../router/v2.router';
import { IAuthStateManager } from '../auth-state-manager/auth-state-manager';
import { AuthStateService } from './auth-state.service';

/**
 * To add a new service:
 *
 * - Create a new service class that implements the Service interface
 * - Add the service name to the KNOWN_SERVICES array
 * - Add a condition to the `createService` method to initialize the service
 */
const AUTO_RENEW = 'autoRenew';
const SYNC_STORAGE = 'syncStorage';
const LEADER_ELECTION = 'leaderElection';
const SINGLE_SIGNOUT = 'singleSignout';
const AUTH_STATE = 'authState';

const KNOWN_SERVICES = [AUTO_RENEW, SYNC_STORAGE, LEADER_ELECTION, SINGLE_SIGNOUT, AUTH_STATE] as const;
type ServiceName = (typeof KNOWN_SERVICES)[number];

/**
 * Handles orchestrating the starting and stopping of services and leaders.
 */
export class ServiceManager {
  private static readonly defaultOptions: ServiceOptions = {
    autoRenew: true,
    syncStorage: true,
    electionChannelName: CHANNEL_NAMES.LEADER_ELECTION,
    singleSignout: true,
  };

  private readonly tokenManager: ITokenManager;
  private readonly authStateManager: IAuthStateManager<any>;
  private readonly router: V2Router;
  private readonly logger = Logger.createLogger(LoggerNames.SERVICE_MANAGER);

  private services: Map<ServiceName, Service>;

  private started: boolean = false;

  public readonly options: ServiceOptions;
  constructor(
    tokenManager: ITokenManager,
    router: V2Router,
    authStateManager: IAuthStateManager<any>,
    options: Partial<ServiceOptions> = {},
  ) {
    this.tokenManager = tokenManager;
    this.router = router;
    this.authStateManager = authStateManager;
    this.onLeader = this.onLeader.bind(this);
    this.options = { ...ServiceManager.defaultOptions, ...options };
    this.services = new Map();

    KNOWN_SERVICES.forEach((service) => {
      const serviceInstance = this.createService(service);
      this.services.set(service, serviceInstance);
    });
  }

  /**
   * Called when the current instance becomes leader. Will start any services
   * that require leadership.
   */
  public async onLeader() {
    if (this.started) {
      this.logger.debug('Appointed leader, starting services');
      await this.startServices();
    }
  }

  /**
   * Checks if any of the known services can start and require leadership.
   * Used to determine whether to begin leader election.
   */
  public isLeaderRequired() {
    return Array.from(this.services.values()).some((service) => service.canStart() && service.requiresLeadership());
  }

  /**
   * Returns whether this instance of ServiceManager is the current leader.
   */
  public isLeader() {
    const leaderElection = this.services.get(LEADER_ELECTION) as LeaderElectionService;
    return leaderElection?.isLeader() ?? false;
  }

  /**
   * Returns whether the current channel has a leader.
   */
  public hasLeader() {
    const leaderElection = this.services.get(LEADER_ELECTION) as LeaderElectionService;
    return leaderElection?.hasLeader() ?? false;
  }

  /**
   * Starts all startable services
   */
  public async start() {
    if (this.started) {
      this.logger.warn('Service manager already started');
    }
    await this.startServices();
    this.started = true;
  }

  /**
   * Stops all running services
   */
  public async stop() {
    await this.stopServices();
    this.started = false;
  }

  /**
   * Return a service instance by name
   * @param name
   */
  public getService(name: ServiceName) {
    return this.services.get(name);
  }

  private async startServices() {
    for (const [name, service] of this.services.entries()) {
      if (this.canStartService(name, service)) {
        this.logger.debug(`Starting service ${name}`);
        await service.start();
      }
    }
  }

  private async stopServices() {
    for (const [name, service] of this.services.entries()) {
      if (service.isStarted()) {
        this.logger.debug(`Stopping service ${name}`);
        await service.stop();
      }
    }
  }

  /**
   * Check if a service can be started. Will account for current status, and
   * whether the service requires leadership.
   * @param name
   * @param service
   * @private
   */
  private canStartService(name: ServiceName, service: Service) {
    let canStart = service.canStart();

    if (name === LEADER_ELECTION) {
      // Don't start the leader election if we don't need one
      canStart &&= this.isLeaderRequired();
    } else if (service.requiresLeadership()) {
      this.logger.debug(`Service ${name} requires leadership. Current leader status: ${this.isLeader()}`);
      // Only start a service that requires leadership if we are currently the leader.
      canStart &&= this.isLeader();
    }
    this.logger.debug('Service', name, 'can start:', canStart);
    return canStart;
  }

  private createService(name: ServiceName): Service {
    switch (name) {
      case LEADER_ELECTION:
        // Provide LeaderElection our onLeader callback so it can start any services that require leadership
        return new LeaderElectionService({ ...this.options, onLeader: this.onLeader });
      case AUTO_RENEW:
        return new AutoRenewalService(this.tokenManager, this.options);
      case SYNC_STORAGE:
        return new SyncStorageService(this.tokenManager, this.options);
      case SINGLE_SIGNOUT:
        return new SingleSignoutService(this.tokenManager, this.router, this.options);
      case AUTH_STATE:
        return new AuthStateService(this.tokenManager, this.authStateManager);
      default:
        return exhaustiveMatch(name, new AuthSdkError(`Unknown service ${name}`));
    }
  }
}
